import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ReturnResult } from 'src/app/shared/models/return-result';
import {
  IContractVaultSummary,
  IContractVaultArtifact,
  IContractVaultSnapshot,
  IGenerateShareLinkResponse,
  IContractShareLinkItem
} from 'src/app/shared/models/v1/contract-vault/contract-vault.model';
import { apiUrl } from 'src/environments/environment';

/**
 * Pure HTTP Service for Contract Vault domain.
 * Communicates with BOF backend ContractVaultController endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class ContractVaultHttpService {
  private readonly baseUrl = `${apiUrl}/ContractVault`;

  constructor(private http: HttpClient) {}

  /**
   * Retrieves the contract vault summary and downloadable artifacts for a proposal.
   * @param proposalProfileId The ID or ExtendGUID of the proposal.
   */
  public getSummary(proposalProfileId: string): Observable<ReturnResult<IContractVaultSummary>> {
    return this.http.get<ReturnResult<IContractVaultSummary>>(`${this.baseUrl}/Summary/${proposalProfileId}`);
  }

  /**
   * Triggers a test or manual sync snapshot ingestion into the vault.
   * @param payload Contract vault sync payload.
   */
  public syncSnapshot(payload: any): Observable<ReturnResult<boolean>> {
    return this.http.post<ReturnResult<boolean>>(`${this.baseUrl}/Sync`, payload);
  }

  /**
   * Retries downloading and cloning a failed artifact into internal BOF storage.
   * @param artifactId Database ID of the artifact.
   */
  public retryCloneArtifact(artifactId: number): Observable<ReturnResult<IContractVaultArtifact>> {
    return this.http.post<ReturnResult<IContractVaultArtifact>>(`${this.baseUrl}/Artifact/${artifactId}/Retry`, {});
  }

  /**
   * Retries cloning all failed or pending artifacts for a proposal.
   * @param proposalProfileId The ID or ExtendGUID of the proposal.
   */
  public retryAllArtifacts(proposalProfileId: string): Observable<ReturnResult<IContractVaultArtifact[]>> {
    return this.http.post<ReturnResult<IContractVaultArtifact[]>>(`${this.baseUrl}/Proposal/${proposalProfileId}/RetryAll`, {});
  }

  /**
   * Generates a secure, time-limited or permanent presigned share link for an artifact.
   * @param artifactId Database ID of the artifact.
   * @param durationMinutes Duration lifespan in minutes (null/0 for permanent).
   * @param label Optional recipient description / note.
   * @param accessType Action scope ("view" or "download").
   */
  public generateShareLink(
    artifactId: number,
    durationMinutes: number | null,
    label?: string,
    accessType: 'view' | 'download' = 'view'
  ): Observable<ReturnResult<IGenerateShareLinkResponse>> {
    return this.http.post<ReturnResult<IGenerateShareLinkResponse>>(
      `${this.baseUrl}/Artifact/${artifactId}/ShareLink`,
      { durationMinutes, label, accessType }
    );
  }

  /**
   * Retrieves all active and revoked share links for an artifact.
   * @param artifactId Database ID of the artifact.
   */
  public getShareLinks(artifactId: number): Observable<ReturnResult<IContractShareLinkItem[]>> {
    return this.http.get<ReturnResult<IContractShareLinkItem[]>>(`${this.baseUrl}/Artifact/${artifactId}/ShareLinks`);
  }

  /**
   * Revokes a specific share link by its database ID.
   * @param linkId Database ID of the share link.
   */
  public revokeShareLink(linkId: number): Observable<ReturnResult<boolean>> {
    return this.http.post<ReturnResult<boolean>>(`${this.baseUrl}/ShareLink/${linkId}/Revoke`, {});
  }

  /**
   * Revokes all active share links for an artifact in 1 click.
   * @param artifactId Database ID of the artifact.
   */
  public revokeAllShareLinks(artifactId: number): Observable<ReturnResult<boolean>> {
    return this.http.post<ReturnResult<boolean>>(`${this.baseUrl}/Artifact/${artifactId}/RevokeAllShareLinks`, {});
  }

  /**
   * Securely downloads an artifact binary stream via Angular HttpClient with authenticated Bearer headers.
   * @param downloadUrl Relative or absolute download endpoint URL.
   */
  public downloadFileBlob(downloadUrl: string): Observable<Blob> {
    const resolvedUrl = this.resolveAbsoluteUrl(downloadUrl);
    return this.http.get(resolvedUrl, { responseType: 'blob' });
  }

  /**
   * Triggers client-side browser file save for downloaded Blob.
   * @param blob Downloaded binary blob.
   * @param fileName Target filename on user machine.
   */
  public saveBlobAsFile(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
  }

  private resolveAbsoluteUrl(url?: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) {
      const host = apiUrl.replace(/\/api(\/v\d+)?\/?$/i, '');
      return `${host}${url}`;
    }
    return `${apiUrl}/${url}`;
  }
}




